import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isToday from 'dayjs/plugin/isToday';
import localeData from 'dayjs/plugin/localeData';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';
import weekday from 'dayjs/plugin/weekday';

let extended = false;

/**
 * Eagerly extends the dayjs prototype with every plugin used in arkestro-client.
 *
 * Why this exists:
 *   dayjs plugins mutate the dayjs prototype. If a module that calls
 *   `dayjs(...).tz(...)` evaluates *before* `dayjs.extend(timezone)` has run,
 *   the call crashes with:
 *
 *     TypeError: Cannot set properties of undefined (setting '$timezone')
 *
 *   That race happened when a page chunk reaching for `.tz()` loaded before
 *   `components/App/App.tsx` mounted (where the historical setup lived). It
 *   produced ~20 production errors per week in Datadog Error Tracking.
 *
 * How this fixes it:
 *   - All dayjs.extend(...) calls happen once, here.
 *   - `pages/_app.tsx`, `components/App/App.tsx`, `utils/BidRequestDefault.js`,
 *     and `test/test_utils.tsx` all import this module instead of doing their
 *     own extend calls.
 *   - Because `pages/_app.tsx` imports this module synchronously near the top
 *     of the file, dayjs is fully extended before any page chunk can race
 *     against it.
 *
 * Plugin order matters for the antd / dayjs integration:
 *   https://stackoverflow.com/q/76650410 — customParseFormat / advancedFormat
 *   must be extended before timezone/utc.
 *
 * Idempotent — calling `initDayjs()` more than once is a no-op.
 *
 * Linked Jira: PEPITA-825
 */
export const initDayjs = (): void => {
  if (extended) return;
  dayjs.extend(customParseFormat);
  dayjs.extend(advancedFormat);
  dayjs.extend(weekday);
  dayjs.extend(localeData);
  dayjs.extend(weekOfYear);
  dayjs.extend(weekYear);
  dayjs.extend(isToday);
  dayjs.extend(utc);
  dayjs.extend(timezone);
  extended = true;
};

// Side-effect: extend on first import so callers who only `import '@utils/dayjsInit'`
// (without calling `initDayjs()` explicitly) still get a fully-extended dayjs.
initDayjs();

export default dayjs;
